"use client";
import React from "react";
import Image from "components/common/Image";
import { get } from "utils/lodash";
import { offerDiscountPct } from "services/campaignPackage/formatter";
import { formatPrice } from "utils/currencyFormatter";
import helper from "utils/helper";
import { roundRating, shortAddress } from "./cardFormat";

/** Ảnh thẻ ưu đãi: ưu tiên ảnh chiến dịch (campaign.gallery), fallback avatar shop */
const offerCover = (pkg: any): string => {
  const avatar = helper.imageUrl(get(pkg, "shop.avatar.url")) || "";
  const gallery = get(pkg, "campaign.gallery", []) || [];
  const raw = get(gallery, "0.sizes.card.url") || get(gallery, "0.url") || "";
  return raw ? helper.imageUrl(raw) || avatar : avatar;
};

/**
 * Card ưu đãi trải nghiệm (campaignPackage type=service).
 * Dùng chung cho section trang chủ (dải cuộn ngang, width cố định) và trang /uu-dai (grid, width auto).
 */
const OfferCard: React.FC<{
  pkg: any;
  onClick?: (pkg: any) => void;
  /** "rail" = dải cuộn ngang w-262 (home); "grid" = full width theo ô lưới */
  layout?: "rail" | "grid";
  /** true cho vài thẻ đầu màn hình (ảnh LCP của /uu-dai) */
  priority?: boolean;
}> = ({ pkg, onClick, layout = "rail", priority = false }) => {
  const shop = get(pkg, "shop");
  const cover = offerCover(pkg);
  const offerPrice = get(pkg, "displayDiscountPrice");
  const originalPrice = get(pkg, "service.menuPrice");
  const discountPct = offerDiscountPct(pkg);
  const rating = roundRating(get(shop, "rating"));
  const serviceName =
    get(pkg, "service.displayName") || get(pkg, "service.name") || get(pkg, "name");
  const shopLine = [get(shop, "name"), shortAddress(get(shop, "displayedAddress"))]
    .filter(Boolean)
    .join(" · ");

  return (
    // div bấm được → thêm role/tabIndex/Enter/Space để dùng được bằng bàn phím.
    // Không đặt aria-label riêng: axe label-content-name-mismatch báo lỗi khi
    // accessible name (aria-label) khác text hiển thị trong thẻ (tên dịch vụ,
    // giá, tên shop…) — bỏ aria-label để tên truy cập = nội dung text hiển thị.
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(pkg)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(pkg);
        }
      }}
      className={`group rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary overflow-hidden border border-gray-100 shadow-sm bg-white flex flex-col cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-primary/40 ${
        // Mobile (mockup A, 05/10): thẻ 150px để thấy 2,3 thẻ/màn; desktop giữ 262px
        layout === "rail" ? "w-[150px] md:w-[262px] flex-shrink-0" : "w-full"
      }`}
    >
      <div className="relative shrink-0">
        <Image
          src={cover}
          alt={serviceName || get(shop, "name") || "Ưu đãi"}
          // Mobile rail: ảnh ngang thấp (112px) thay vì 4:3 để tiết kiệm chiều dọc
          wrapClassName={`w-full bg-gray-100 overflow-hidden ${
            layout === "rail" ? "h-[112px] md:h-auto md:aspect-[4/3]" : "aspect-[4/3]"
          }`}
          className="transition-transform duration-300 group-hover:scale-[1.04]"
          priority={priority}
          sizes={
            layout === "rail"
              ? "(min-width: 768px) 262px, 150px"
              : "(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
          }
        />
        {discountPct > 0 && (
          <span className="absolute left-2 top-2 md:left-3 md:top-3 rounded-full bg-red-500 px-2 py-0.5 md:px-2.5 md:py-1 text-xs font-bold text-white shadow">
            -{discountPct}%
          </span>
        )}
        {rating && (
          <span className="absolute right-2 top-2 md:right-3 md:top-3 rounded-full bg-white px-2 py-0.5 text-xs font-bold text-primary shadow">
            {rating}
          </span>
        )}
      </div>
      <div className="p-2.5 md:p-3.5 flex flex-col gap-1 flex-1">
        {/* line-clamp-2 từng bị kẹt display:flow-root → lộ nửa dòng 3. Khoá cứng 2 dòng bằng max-h + overflow. */}
        <h3
          className={`font-bold text-black01 line-clamp-2 [display:-webkit-box] h-[2.6em] max-h-[2.6em] overflow-hidden leading-[1.3] !mb-0 ${
            layout === "rail" ? "text-[13px] md:text-[15px]" : "text-[14px] md:text-[15px]"
          }`}
        >
          {serviceName}
        </h3>
        <p className="text-xs text-gray-500 truncate !mb-0">{shopLine}</p>
        <div className="flex flex-wrap items-end gap-x-1.5 md:gap-x-2 mt-1 md:mt-1.5 min-h-[1.5rem]">
          {offerPrice ? (
            <p
              className={`text-primary-ink font-extrabold whitespace-nowrap !mb-0 ${
                layout === "rail" ? "text-[15px] md:text-lg" : "text-[16px] md:text-lg"
              }`}
            >
              {formatPrice(offerPrice)}
            </p>
          ) : null}
          {originalPrice && originalPrice > offerPrice ? (
            <p className="text-gray-400 line-through text-xs pb-0.5 md:pb-1 whitespace-nowrap !mb-0">
              {formatPrice(originalPrice)}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default OfferCard;
